import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ContentChild, ElementRef, EventEmitter, Input, OnInit, Output, SimpleChanges, TemplateRef, ViewChild, ViewChildren } from '@angular/core';
import { NbMenuItem, NbMenuService, NbPosition } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { IFile } from '@shared/interfaces/file/file';
import { StorageService } from '@shared/services/storage.service';
import { filter, Subscription, take } from 'rxjs';
import { FileDisplayType } from '../../enums/file-display-type.enum';
import { FileSortFields } from '../../enums/file-sort-fields.enum';
import { Breadcrumbs } from '../../interfaces/breadcrumbs.interface';
import { IFileDropEvent } from '../../interfaces/file-drop-event.interface';
import { IFileSort } from '../../interfaces/file-sort.interface';

@Component({
  selector: 'app-file-browser',
  templateUrl: './file-browser.component.html',
  styleUrls: ['./file-browser.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FileBrowserComponent implements OnInit {
  @ContentChild('previewTemplate') previewTemplate: TemplateRef<any>;
  @ContentChild('infoTemplate') infoTemplate: TemplateRef<any>;

  @ViewChild('bc') breadcrumbsElement: ElementRef<HTMLDivElement>;

  @Input() files: IFile[] = [];
  @Input() isLoading: boolean;
  @Input() rootPath: string = 'My files';
  @Input() compactMode: boolean;
  @Input() multiSelect: boolean;
  @Input() displayType: FileDisplayType = FileDisplayType.PREVIEW_BLOCKS;

  @Input() enableDrag: boolean;
  @Input() enableUnlock: boolean;
  @Input() enableShare: boolean;
  @Input() enablePrint: boolean;
  @Input() enableDelete: boolean;
  @Input() enableDownload: boolean;
  
  @Output() onPrint = new EventEmitter<IFile>();
  @Output() onShare = new EventEmitter<IFile>();
  @Output() onDownload = new EventEmitter<IFile>();
  @Output() onUnlock = new EventEmitter<IFile>();
  @Output() onDelete = new EventEmitter<IFile>();

  @Output() onDeleteSelected = new EventEmitter<IFile[]>();
  
  @Output() onLoadNewPage = new EventEmitter<string>();
  @Output() onLoadDirectory = new EventEmitter<IFile | undefined>();
  @Output() onFileDropped = new EventEmitter<IFileDropEvent>();

  @Input() breadcrumbs: Breadcrumbs;
  @Output() breadcrumbsChange = new EventEmitter<Breadcrumbs>();

  @Input() sort: IFileSort = { field: FileSortFields.NAME, direction: 'asc' };
  @Output() sortChange = new EventEmitter<IFileSort>();
  
  private subscription = new Subscription();

  protected sortFields = FileSortFields;
  
  protected readonly dragDelay = 100;
  protected readonly displayTypes = FileDisplayType;
  protected readonly menuName = `FILE_BROWSER_MENU_${Math.random()}`;
  
  protected selectedFiles: IFile[] = [];
  protected actions: NbMenuItem[] = [];

  constructor(
    private changeDetector: ChangeDetectorRef,
    private storageService: StorageService,
    private translateService: TranslateService,
    private nbMenuService: NbMenuService
  ) { }

  /** Used to skip parent drop zone of the folder list */
  protected get folderLists(): string[] {
    return this.files
      .filter(file => file.isCatalog)
      .map(file => file.id);
  }

  protected get icon(): string {
    return this.sort.direction === 'asc' ? 'arrow-up' : 'arrow-down';
  }

  protected get selectedSize(): number {
    return this.selectedFiles.reduce((previous, current) => previous + current.size, 0);
  }
  
  protected get filesTranslation(): string {
    return this.selectedFiles.length === 1 ? 'common.files' : 'common.file';
  }

  protected get previewBlocksEnabled(): boolean {
    return this.displayType === FileDisplayType.PREVIEW_BLOCKS;
  }

  ngOnInit(): void {
    this.enableDelete && this.actions.push({ icon: 'trash-2-outline', title: this.translateService.instant('common.delete'), data: 'onDeleteSelected' });

    this.subscription.add(
      this.nbMenuService
        .onItemClick()
        .pipe(filter(data => data.tag === this.menuName))
        .subscribe(data => this[data.item.data].next(this.selectedFiles))
    );

    this.breadcrumbs = [{ id: '', name: this.rootPath }];
    this.breadcrumbsChange.next(this.breadcrumbs);

    this.displayType = this.displayType || this.storageService.getStorage(this.storageService.fileBrowserView);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  protected scrollBreadcrumbs(speed: number): void {
    this.breadcrumbsElement.nativeElement.scrollBy({ left: speed, behavior: 'smooth' });
  }

  protected onBreadcrumbScroll(): void {
    this.changeDetector.detectChanges();
  }

  protected trackByFn(index: number, item: IFile): string {
    return item.id;
  }

  protected onScroll(event: Event): void {
    const target = event.target as HTMLDivElement;
    const currentDirectory = this.breadcrumbs[this.breadcrumbs.length - 1].id;
    
    if (target.offsetHeight + target.scrollTop >= target.scrollHeight) {
      this.onLoadNewPage.next(currentDirectory);
    }
  }

  protected goBackPage(id: string): void {
    const index = this.breadcrumbs.findIndex(crumb => crumb.id === id);

    this.selectedFiles = [];
    this.breadcrumbs.splice(index + 1);
    this.onLoadDirectory.next(this.breadcrumbs[index].file);

    this.changeDetector.detectChanges();
  }

  protected goNextPage(file: IFile): void {
    if (!file.isCatalog) {
      return;
    }
    
    this.selectedFiles = [];
    this.breadcrumbs.push({ id: file.id, name: file.name, file });

    this.onLoadDirectory.next(file);
    this.breadcrumbsChange.next(this.breadcrumbs);

    this.changeDetector.detectChanges();

    this.breadcrumbsElement.nativeElement.scrollBy({
      left: this.breadcrumbsElement.nativeElement.scrollWidth,
      behavior: 'smooth'
    });
  }

  protected onFileDrop(event: CdkDragDrop<IFile>, newFolder: IFile | string): void {
    const folder = typeof newFolder === 'string' ? { id: newFolder } as IFile : newFolder
    this.onFileDropped.next({ file: event.item.data, folder });
  }

  protected changeFileSelection(selected: boolean, file: IFile): void {
    if (selected) {
      this.selectedFiles.push(file);
      return;
    }
    
    const index = this.selectedFiles.indexOf(file);
    this.selectedFiles.splice(index, 1);
  }

  protected changeSelection(selected: boolean): void {
    if (!selected) {
      this.selectedFiles = [];
      return;
    }

    this.selectedFiles = Object.assign([], this.files);
  }

  protected changeSort(field: FileSortFields): void {
    const newSort: IFileSort = { field, direction: 'asc' };
    
    if (field === this.sort.field) {
      newSort.direction = this.sort.direction === 'asc' ? 'desc' : 'asc';
    } else {
      newSort.direction = 'asc';
    }
    
    newSort.field = field;

    this.sort = newSort;
    this.sortChange.next(this.sort);
  }
}
